// Entrata Users Input Component for Desktop - matching admin-web pattern
import React from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { IEntrataUser } from '@/types/admin/organizations';
import { SVG_COMPONENTS } from '@/assets/admin';

interface EntrataUsersInputProps {
  users: IEntrataUser[];
  onChange: (users: IEntrataUser[]) => void;
  disabled?: boolean;
  addButtonText?: string;
  emptyStateText?: string;
}

export const EntrataUsersInput: React.FC<EntrataUsersInputProps> = ({
  users,
  onChange,
  disabled = false,
  addButtonText = '+ Add User',
  emptyStateText = 'No Entrata API users configured. Click "Add User" to add credentials.',
}) => {
  const handleAddUser = () => {
    onChange([...users, { username: '', password: '' }]);
  };

  const handleRemoveUser = (index: number) => {
    const newUsers = users.filter((_, i) => i !== index);
    onChange(newUsers);
  };

  const handleUpdateUser = (index: number, field: 'username' | 'password', value: string) => {
    const newUsers = [...users];
    newUsers[index] = { ...newUsers[index], [field]: value };
    onChange(newUsers);
  };

  return (
    <View style={styles.container}>
      {/* Header with Add Button */}
      <View style={styles.header}>
        <Text style={styles.sectionTitle}>Entrata API Users</Text>
        <TouchableOpacity
          style={[styles.addButton, disabled && styles.disabledButton]}
          onPress={handleAddUser}
          disabled={disabled}
          activeOpacity={0.7}
        >
          <Text style={styles.addButtonText}>{addButtonText}</Text>
        </TouchableOpacity>
      </View>

      {/* User List */}
      {users.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateText}>
            {emptyStateText}
          </Text>
        </View>
      ) : (
        <View style={styles.usersList}>
          {users.map((user, index) => (
            <View key={index} style={styles.userCard}>
              {/* User Card Header */}
              <View style={styles.userCardHeader}>
                <View style={styles.userIconWrapper}>
                  <SVG_COMPONENTS.ADMIN width={16} height={16} color={ADMIN_COLORS.TEXT.SECONDARY} />
                  <Text style={styles.userLabel}>User {index + 1}</Text>
                </View>
                <TouchableOpacity
                  style={styles.deleteButton}
                  onPress={() => handleRemoveUser(index)}
                  disabled={disabled}
                  activeOpacity={0.7}
                >
                  <Text style={styles.deleteButtonText}>✕</Text>
                </TouchableOpacity>
              </View>

              {/* Username Field */}
              <View style={styles.field}>
                <Text style={styles.label}>Username *</Text>
                <TextInput
                  style={styles.input}
                  value={user.username}
                  onChangeText={(text) => handleUpdateUser(index, 'username', text)}
                  placeholder="Enter username"
                  placeholderTextColor={ADMIN_COLORS.TEXT.PRIMARY}
                  editable={!disabled}
                  autoCapitalize="none"
                />
              </View>

              {/* Password Field */}
              <View style={styles.field}>
                <Text style={styles.label}>Password *</Text>
                <TextInput
                  style={styles.input}
                  value={user.password}
                  onChangeText={(text) => handleUpdateUser(index, 'password', text)}
                  placeholder="Enter password"
                  placeholderTextColor={ADMIN_COLORS.TEXT.PRIMARY}
                  secureTextEntry={true}
                  editable={!disabled}
                  autoCapitalize="none"
                />
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  addButton: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: ADMIN_COLORS.BUTTON.ADD_BUTTON_BACKGROUND,
    borderRadius: 6,
  },
  addButtonText: {
    color: ADMIN_COLORS.BUTTON.PRIMARY_TEXT,
    fontSize: 13,
    fontWeight: '600',
  },
  disabledButton: {
    opacity: 0.5,
  },
  emptyState: {
    padding: 24,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderStyle: 'dashed',
  },
  emptyStateText: {
    fontSize: 13,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    textAlign: 'center',
  },
  usersList: {
    gap: 12,
  },
  userCard: {
    padding: 16,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
  },
  userCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  userIconWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  userLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
  },
  deleteButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: ADMIN_COLORS.BACKGROUND.SECONDARY,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deleteButtonText: {
    fontSize: 18,
    color: ADMIN_COLORS.TEXT.SECONDARY,
    lineHeight: 18,
  },
  field: {
    marginBottom: 12,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: ADMIN_COLORS.TEXT.PRIMARY,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.BORDER,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    color: ADMIN_COLORS.TEXT.PRIMARY,
    backgroundColor: ADMIN_COLORS.BACKGROUND.PRIMARY,
  },
});

